import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, PlayCircle, Search, Loader2, Filter, X } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  pintura: "Pintura",
  drywall: "Drywall / Gesso",
  eletrica: "Elétrica",
  hidraulica: "Hidráulica",
  alvenaria: "Alvenaria",
  limpeza: "Limpeza / Jardinagem",
  pedreiro: "Pedreiro",
  mobiliario: "Mobiliário",
  cobertura: "Cobertura / Telhado",
  portas: "Portas e Janelas",
  piso: "Piso",
  marcenaria: "Marcenaria",
  outros: "Outros",
};

const prettyCategory = (c) => CATEGORY_LABELS[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "Sem categoria");

/**
 * Galeria — public page listing ALL landing media with filters by category / school.
 * Reached from "Ver todas" on the LandingPage.
 */
export default function Galeria() {
  const [items, setItems] = useState([]);
  const [schools, setSchools] = useState({});
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [schoolId, setSchoolId] = useState("all");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/landing/media?limit=500&section=trabalhos");
        // Keep only "trabalhos" (real-service photos/videos). The mockups
        // of ``portal_escola`` / ``painel_admin`` live only on the landing
        // page — this public gallery is for actual service work.
        setItems(
          (r.data || []).filter(
            (x) => !x.is_deleted && (x.section || "trabalhos") === "trabalhos",
          ),
        );
        // Fetch school names lazily via public endpoint? Fall back to names baked in captions.
      } catch (e) {
        // Silent fail — empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categoriesWithCount = useMemo(() => {
    const map = { all: items.length };
    items.forEach((it) => {
      const key = it.category || "sem-categoria";
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [items]);

  const schoolList = useMemo(() => {
    const set = new Set(items.map((i) => i.school_id).filter(Boolean));
    return Array.from(set);
  }, [items]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) => {
      if (cat !== "all") {
        const key = it.category || "sem-categoria";
        if (key !== cat) return false;
      }
      if (schoolId !== "all" && it.school_id !== schoolId) return false;
      if (s && !((it.caption || "") + " " + (it.category || "")).toLowerCase().includes(s)) return false;
      return true;
    });
  }, [items, cat, schoolId, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((it) => {
      const k = it.category || "sem-categoria";
      (g[k] = g[k] || []).push(it);
    });
    return g;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 backdrop-blur bg-white/95">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="shrink-0 text-slate-600 hover:text-[#1e3a8a] p-1.5 rounded-full hover:bg-slate-100" data-testid="btn-back-home">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-lg font-black text-[#1e3a8a] leading-tight truncate">Galeria de trabalhos</h1>
            <p className="text-[11px] text-slate-500 truncate">
              {filtered.length} {filtered.length === 1 ? "item" : "itens"} · navegue por categoria ou escola
            </p>
          </div>
        </div>
        {/* Filters */}
        <div className="max-w-6xl mx-auto px-4 pb-3 space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por legenda, serviço ou categoria..."
              className="w-full h-9 pl-9 pr-3 rounded-full border border-slate-200 text-sm bg-slate-50 focus:bg-white focus:border-[#1e3a8a] outline-none"
              data-testid="galeria-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
            {["all", ...Object.keys(categoriesWithCount).filter((k) => k !== "all")].map((k) => (
              <button
                key={k}
                onClick={() => setCat(k)}
                data-testid={`galeria-cat-${k}`}
                className={`shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors ${
                  cat === k
                    ? "bg-[#1e3a8a] border-[#1e3a8a] text-white shadow-sm"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
              >
                {k === "all" ? "Todas" : prettyCategory(k === "sem-categoria" ? null : k)}
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                  cat === k ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
                }`}>
                  {categoriesWithCount[k] || 0}
                </span>
              </button>
            ))}
          </div>
          {schoolList.length > 1 && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
              <button
                onClick={() => setSchoolId("all")}
                className={`shrink-0 h-7 px-2.5 rounded-full border text-[11px] font-semibold ${
                  schoolId === "all" ? "bg-[#7c3aed] border-[#7c3aed] text-white" : "bg-white border-slate-200 text-slate-600"
                }`}
              >
                Todas escolas
              </button>
              {schoolList.map((sid) => (
                <button
                  key={sid}
                  onClick={() => setSchoolId(sid)}
                  className={`shrink-0 h-7 px-2.5 rounded-full border text-[11px] font-semibold ${
                    schoolId === sid ? "bg-[#7c3aed] border-[#7c3aed] text-white" : "bg-white border-slate-200 text-slate-600"
                  }`}
                >
                  {(items.find((i) => i.school_id === sid) || {}).school_name || schools[sid] || sid.slice(0, 8)}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5">
        {loading ? (
          <div className="py-16 text-center text-slate-500 text-sm">
            <Loader2 className="w-6 h-6 inline mr-2 animate-spin" /> Carregando trabalhos...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center bg-white rounded-2xl border border-slate-100">
            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">
              {search || cat !== "all" || schoolId !== "all"
                ? "Nenhum trabalho corresponde ao filtro."
                : "Ainda não publicamos trabalhos na galeria."}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([catKey, list]) => (
              <section key={catKey} data-testid={`galeria-section-${catKey}`}>
                <div className="flex items-end justify-between mb-3">
                  <h2 className="font-heading text-base font-black text-[#1e3a8a] flex items-center gap-2">
                    <span className="w-1 h-6 bg-[#7c3aed] rounded-full" />
                    {prettyCategory(catKey === "sem-categoria" ? null : catKey)}
                  </h2>
                  <span className="text-[11px] font-bold text-slate-400">{list.length}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {list.map((it) => (
                    <button
                      key={it.id}
                      onClick={() => setPreview(it)}
                      data-testid={`galeria-item-${it.id}`}
                      className="group relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200 active:scale-98 transition-transform"
                    >
                      {it.kind === "video" ? (
                        <>
                          <video
                            src={`${BACKEND}/api/files/${it.storage_path}`}
                            muted
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-80 group-hover:opacity-100">
                            <PlayCircle className="w-10 h-10 text-white drop-shadow" />
                          </div>
                        </>
                      ) : (
                        <img
                          src={`${BACKEND}/api/files/${it.storage_path}`}
                          alt={it.caption || ""}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      )}
                      {(it.caption || it.school_name) && (
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-left">
                          {it.caption && (
                            <p className="text-white text-[10px] font-semibold line-clamp-2 leading-tight">
                              {it.caption}
                            </p>
                          )}
                          {it.school_name && (
                            <p className="text-white/80 text-[9px] font-bold uppercase tracking-wide truncate mt-0.5" data-testid={`galeria-school-${it.id}`}>
                              {it.school_name}
                            </p>
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Preview modal */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          data-testid="galeria-preview"
        >
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
          {preview.kind === "video" ? (
            <video src={`${BACKEND}/api/files/${preview.storage_path}`} controls autoPlay playsInline className="max-h-[90vh] max-w-full rounded-lg" />
          ) : (
            <img src={`${BACKEND}/api/files/${preview.storage_path}`} alt={preview.caption || ""} className="max-h-[90vh] max-w-full rounded-lg" />
          )}
          {(preview.caption || preview.school_name) && (
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              {preview.caption && <p className="text-white text-sm font-semibold text-center max-w-2xl mx-auto">{preview.caption}</p>}
              {preview.school_name && <p className="text-white/80 text-xs font-bold uppercase tracking-wide text-center mt-1">{preview.school_name}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
